import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiPost } from "~/shared/api/client";
import { presignAndUpload } from "~/shared/lib/uploads/presign-and-upload";
import { projectsKeys, type AttachMediaPayload, type ProjectMedia, type ProjectMediaKind } from "./api-types";

// ---------------------------------------------------------------------------
// Fetch functions — project media (posters / teasers)
// ---------------------------------------------------------------------------

export async function attachProjectMedia(
    projectId: string,
    payload: AttachMediaPayload,
): Promise<ProjectMedia> {
    return apiPost<ProjectMedia>(`/v2/projects/${projectId}/media`, payload);
}

export async function deleteProjectMedia(projectId: string, mediaId: string): Promise<void> {
    await apiDelete<{ message: string }>(`/v2/projects/${projectId}/media/${mediaId}`);
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

interface UploadMediaInput {
    projectId: string;
    file: File;
    mediaType: ProjectMediaKind;
}

function detectKind(mediaType: ProjectMediaKind, file: File): "image" | "video" {
    if (mediaType === "teaser") return "video";
    if (file.type.startsWith("video/")) return "video";
    return "image";
}

function destinationFor(mediaType: ProjectMediaKind): "project_poster" | "project_teaser" {
    return mediaType === "poster" ? "project_poster" : "project_teaser";
}

export function useUploadProjectMedia() {
    const qc = useQueryClient();
    return useMutation<ProjectMedia, Error, UploadMediaInput>({
        mutationFn: async ({ projectId, file, mediaType }) => {
            const { s3Url, s3Key } = await presignAndUpload(file, {
                destination: destinationFor(mediaType),
                kind: detectKind(mediaType, file),
                mimeType: file.type || (mediaType === "poster" ? "image/jpeg" : "video/mp4"),
                originFilename: mediaType,
                projectId,
            });
            return attachProjectMedia(projectId, {
                mediaType,
                s3Url,
                s3Key,
                mimeType: file.type || null,
                fileSize: file.size,
            });
        },
        onSuccess: (_, vars) => {
            void qc.invalidateQueries({ queryKey: projectsKeys.byId(vars.projectId) });
        },
    });
}

export function useDeleteProjectMedia(projectId: string) {
    const qc = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: (mediaId) => deleteProjectMedia(projectId, mediaId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: projectsKeys.byId(projectId) });
        },
    });
}
